<template>
  <div id="income">
    <DebtorTags :has-codebtor="simulation.prospect.hasCodebtor" />
    <div class="columns">
      <div class="column">
        <div data-cy="income-data" class="field-separation mb-5">
          <p>¿Qué tipo de ingresos tiene?</p>
          <div class="columns">
            <div class="column is-4-desktop income-type">
              <b-select
                v-model="incomeType"
                placeholder="Seleccione una opción"
                size="is-small"
                data-cy="income-type"
                expanded>
                <option value="VARIABLE_TAXABLE">
                  Variable imponible
                </option>
                <option value="FIXED_TAXABLE">
                  Renta fija
                </option>
              </b-select>
              <small v-if="incomeType === 'VARIABLE_TAXABLE'">Ingrese los últimos tres montos
                variables</small>
            </div>
            <div v-if="incomeType === 'VARIABLE_TAXABLE'" class="column is-8-desktop">
              <div class="inner pb-2">
                <div class="inner-col">
                  <b-field class="is-small">
                    <p class="control">
                      <span class="button is-static">$</span>
                    </p>
                    <b-input
                      v-model="variableMonthOne"
                      size="is-small"
                      type="number"
                      placeholder="0,00"
                      expanded
                      data-cy="variable-month-one"
                      @input.native="onSelectChange(incomeType, variableMonthOne, 0)" />
                  </b-field>
                </div>
                <div class="inner-col">
                  <b-field class="is-small">
                    <p class="control">
                      <span class="button is-static">$</span>
                    </p>
                    <b-input
                      v-model="variableMonthTwo"
                      size="is-small"
                      type="number"
                      placeholder="0,00"
                      expanded
                      data-cy="variable-month-two"
                      @input.native="onSelectChange(incomeType, variableMonthTwo, 1)" />
                  </b-field>
                </div>
                <div class="inner-col">
                  <b-field class="is-small">
                    <p class="control">
                      <span class="button is-static">$</span>
                    </p>
                    <b-input
                      v-model="variableMonthThree"
                      size="is-small"
                      type="number"
                      placeholder="0,00"
                      expanded
                      data-cy="variable-month-three"
                      @input.native="onSelectChange(incomeType, variableMonthThree, 2)" />
                  </b-field>
                </div>
              </div>
            </div>
            <div v-if="incomeType === 'FIXED_TAXABLE'" class="column">
              <div class="inner">
                <div class="inner-col">
                  <b-field class="is-small">
                    <p class="control">
                      <span class="button is-static">$</span>
                    </p>
                    <b-input
                      v-model="fixedMonthOne"
                      size="is-small"
                      type="number"
                      placeholder="0,00"
                      expanded
                      @input.native="onSelectChange(incomeType, fixedMonthOne, 0)" />
                  </b-field>
                </div>
                <div class="column" />
                <div class="column" />
              </div>
            </div>
          </div>
        </div>
        <div
          v-for="(config, index) in incomeConfig"
          :key="config.id"
          class="evaluation-field-container field-separation">
          <verify-inputs
            :id="config.id"
            v-model="config.value"
            :data-cy="`conf-${config.id}`"
            :label="config.label"
            :label-description="config.labelDescription"
            :visible.sync="config.visible"
            :template="config.template"
            :is-array="config.isArray"
            @switch-change="onSwitchChange($event, config.id)"
            @input.native="onInputChange($event.target, config.id)"
            @add-input="addInput(index)"
            @remove-input="removeInput(index, $event)" />
        </div>
      </div>
      <div class="column pl-5">
        <TableSummary
          data-cy="income-result"
          title="Renta promedio calculada"
          :icon-label="totalIncomeCardSummary"
          show-icon-label
          :data="incomeCardSummary" />
        <DocumentsVerification
          :label="label"
          :type="'income'"
          :evaluation-id="evaluationId"
          :one-client-id="oneClientId"
          :validate-documents="hasNewIncome"
          @documents-uploaded="handleDocumentsUploaded" />
      </div>
    </div>
  </div>
</template>

<script>

import VerifyInputs from '@/ui/components/internal/evaluations/verify/VerifyInputs.vue';
import {
  FinancialDataVerificatorClient
} from '@/application/services/evaluations/apis/financial-data-verificator-client';
import TableSummary from '@/ui/components/internal/evaluations/TableSummary.vue';
import { formatValueToCurrency } from '@/application/services/utils/currency-formatter';
import { mapState } from 'vuex';
import DebtorTags from '@/ui/components/internal/evaluations/verify/DebtorTags.vue';
import {
  EvaluationDataProcessor
} from '@/application/services/evaluations/evaluation-data-processor';
import DocumentsVerification
  from '@/ui/components/internal/evaluations/verify/DocumentsVerification.vue';
import { getSafe } from '@/application/services/get-safe';

export default {
  name: 'Income',
  components: { DocumentsVerification, TableSummary, VerifyInputs, DebtorTags },
  props: {
    label: {
      type: String,
      required: false,
      default: 'Income'
    },
    evaluationId: {
      type: String,
      required: true
    },
    oneClientId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      incomeConfig: [
        {
          id: 'fixedNonTaxableIncome',
          label: '¿Posee otros items de ingreso?',
          labelDescription: '(Colación, movilización...)',
          visible: false,
          isArray: true,
          template: [
            {
              name: 'description',
              placeholder: 'Descripción',
              type: 'input'
            }, {
              name: 'monthOne',
              placeholder: '0,00',
              type: 'number'
            }
          ],
          value: [{}]
        },
        {
          id: 'otherDeductions',
          label: '¿Posee otros items de descuento?',
          labelDescription: '(Retenciones judiciales, seguros adicionales...)',
          visible: false,
          isArray: true,
          template: [
            {
              name: 'description',
              placeholder: 'Descripción',
              type: 'input'
            },
            {
              name: 'monthlyAmount',
              placeholder: 'Mensualidad',
              type: 'number'
            }
          ],
          value: [{}]
        },
        {
          id: 'periodicVariableTaxableIncome',
          label: '¿Recibe aporte esporádico?',
          labelDescription: '(Bonos, incentivos de venta...)',
          visible: false,
          isArray: true,
          template: [
            {
              name: 'description',
              placeholder: 'Descripción',
              type: 'input'
            },
            {
              name: 'periodicityInMonths',
              placeholder: 'Frecuencia',
              type: 'select',
              options: [
                { value: 1, label: 'Mensual' },
                { value: 2, label: 'Bimensual' },
                { value: 3, label: 'Trimestral' },
                { value: 6, label: 'Semestral' },
                { value: 12, label: 'Anual' }
              ]
            },
            {
              name: 'amount',
              placeholder: 'Monto',
              type: 'number'
            }
          ],
          value: [{}]
        }
      ],
      incomeId: null,
      incomeType: undefined,
      variableMonthOne: null,
      variableMonthTwo: null,
      variableMonthThree: null,
      fixedMonthOne: null,
      bodyIncome: {
        fixedTaxableIncome: [],
        variableTaxableIncome: [{}],
        periodicVariableTaxableIncome: [],
        fixedNonTaxableIncome: [],
        otherDeductions: []
      },
      financialDataVerificatorClient: null,
      validateDocuments: false,
      hasNewIncome: false,
      switchVisibility: [],
      initialIncome: null
    };
  },
  computed: {
    ...mapState('evaluation', ['incomeCardSummary', 'totalIncomeCardSummary', 'idNumber', 'simulation', 'income', 'manualEvaluationId',
      'parentEvaluationId', 'evaluationType']),
    isAutomatic() {
      return this.evaluationType === 'automatic';
    }
  },
  watch: {
    incomeType(val) {
      let labelAmount = 'Monto fijo';
      if (val === 'VARIABLE_TAXABLE') {
        this.bodyIncome.fixedTaxableIncome = [];
        this.fixedMonthOne = null;
        labelAmount = 'Monto líquido';
      } else if (val === 'FIXED_TAXABLE') {
        this.bodyIncome.variableTaxableIncome = [];
        if (this.isAutomatic && getSafe(this.simulation, 'offerInputs.disposableIncome', null)) {
          this.bodyIncome.fixedTaxableIncome = [
            {
              description: 'FIXED_TAXABLE',
              monthlyAmount: getSafe(this.simulation, 'offerInputs.disposableIncome', null)
            }
          ];
          this.fixedMonthOne = getSafe(this.simulation, 'offerInputs.disposableIncome', null);
        }
      }
      this.$store.commit('evaluation/setIncomeAmount', labelAmount);
      this.calculateIncome(this.bodyIncome);
    }
  },
  created() {
    this.financialDataVerificatorClient = new FinancialDataVerificatorClient();
  },
  async mounted() {
    this.incomeType = this.isAutomatic ? 'FIXED_TAXABLE' : 'VARIABLE_TAXABLE';
    await this.setInitialData();
  },
  methods: {
    async setInitialData() {
      if (!this.isAutomatic && this.income && this.income.length > 0) {
        const { variableTaxableIncome,
          fixedTaxableIncome,
          periodicVariableTaxableIncome,
          fixedNonTaxableIncome,
          otherDeductions } = this.income[0].incomes;

        this.bodyIncome.fixedTaxableIncome = fixedTaxableIncome;
        if (fixedTaxableIncome.length > 0) {
          this.fixedMonthOne = fixedTaxableIncome[0].monthlyAmount;
          this.incomeType = 'FIXED_TAXABLE';
        }
        this.bodyIncome.variableTaxableIncome = variableTaxableIncome;
        if (variableTaxableIncome.length > 0) {
          this.variableMonthOne = variableTaxableIncome[0].monthOne;
          this.variableMonthTwo = variableTaxableIncome[0].monthTwo;
          this.variableMonthThree = variableTaxableIncome[0].monthThree;
          this.incomeType = 'VARIABLE_TAXABLE';
        }

        this.bodyIncome.periodicVariableTaxableIncome = periodicVariableTaxableIncome;
        if (periodicVariableTaxableIncome.length > 0) {
          this.incomeConfig[2].visible = true;
          this.incomeConfig[2].value = periodicVariableTaxableIncome;
        }

        this.bodyIncome.fixedNonTaxableIncome = fixedNonTaxableIncome;
        if (fixedNonTaxableIncome.length > 0) {
          this.incomeConfig[0].visible = true;
          this.incomeConfig[0].value = fixedNonTaxableIncome;
        }

        this.bodyIncome.otherDeductions = otherDeductions;
        if (otherDeductions.length > 0) {
          this.incomeConfig[1].visible = true;
          this.incomeConfig[1].value = otherDeductions;
        }
        await this.calculateIncome(this.bodyIncome);
      }
      this.initialIncome = {
        fixedMonthOne: this.fixedMonthOne,
        variableMonthOne: this.variableMonthOne,
        variableMonthTwo: this.variableMonthTwo,
        variableMonthThree: this.variableMonthThree
      };
    },
    variableTaxableHandler(index, value) {
      if (this.incomeType !== 'VARIABLE_TAXABLE') {
        return;
      }
      const monthOne = this.bodyIncome.variableTaxableIncome[0] ? this.bodyIncome.variableTaxableIncome[0].monthOne : 0;
      const monthTwo = this.bodyIncome.variableTaxableIncome[0] ? this.bodyIncome.variableTaxableIncome[0].monthTwo : 0;
      const monthThree = this.bodyIncome.variableTaxableIncome[0] ? this.bodyIncome.variableTaxableIncome[0].monthThree : 0;
      switch (index) {
        case 0:
          this.bodyIncome.variableTaxableIncome[0] = {
            description: 'Ingreso variable',
            monthOne: +value,
            monthTwo,
            monthThree
          };
          break;
        case 1:
          this.bodyIncome.variableTaxableIncome[0] = {
            description: 'Ingreso variable',
            monthOne,
            monthTwo: +value,
            monthThree
          };
          break;
        case 2:
          this.bodyIncome.variableTaxableIncome[0] = {
            description: 'Ingreso variable',
            monthOne,
            monthTwo,
            monthThree: +value
          };
          break;
        default:
          break;
      }
      this.bodyIncome.fixedTaxableIncome = [];
    },
    fixedTaxableHandler(index, pie, isEmpty) {
      if (this.incomeType !== 'FIXED_TAXABLE') {
        return;
      }

      this.bodyIncome.fixedTaxableIncome[index] = {
        description: 'Renta fija',
        monthlyAmount: Number(pie)
      };
      if (!isEmpty) {
        this.bodyIncome.fixedTaxableIncome[index] = {
          description: this.incomeType,
          monthlyAmount: Number(pie)
        };
      } else {
        this.bodyIncome.fixedTaxableIncome.splice(index, 1);
      }
      this.bodyIncome.variableTaxableIncome = [];
    },
    async onSelectChange(incomeType, pie, index) {
      const isEmpty = pie.length === 0;
      const value = isEmpty ? 0 : pie;
      this.variableTaxableHandler(index, value);
      this.fixedTaxableHandler(index, pie, isEmpty);
      await this.calculateIncome(this.bodyIncome);

      this.updateIncomeState();
    },
    async onInputChange(type, name) {
      if (type.attributes[0].value !== 'checkbox') {
        const incomeTarget = this.incomeConfig.find(income => income.id === name);
        this.bodyIncome[name] = this.$getSafe(incomeTarget, 'value', [{}]);
        await this.calculateIncome(
          this.bodyIncome
        );
      }
    },
    async onSwitchChange(checked, name) {
      this.bodyIncome[name] = checked ? this.incomeConfig.find(income => income.id === name).value : [];
      this.switchVisibility[name] = checked;
      if (checked) {
        await this.calculateIncome(this.bodyIncome);
      }
      this.updateIncomeState();
    },
    addInput(i) {
      this.incomeConfig[i].value.push({});
    },
    async removeInput(index, _indexRow) {
      if (index < 0) return;

      this.incomeConfig[index].value.splice(_indexRow, 1);
      await this.calculateIncome(this.bodyIncome);
    },
    async update() {
      const income = this.incomeConfig.reduce((acc, config) => {
        acc[config.id] = config.value;
        return acc;
      }, []);
      const params = {
        data: {
          type: 'income',
          attributes: {
            ...income,
            status: 'CLOSED',
            version: 1,
            userId: this.$store.state.currentUser.user._id
          }
        }
      };

      if (this.incomeId) {
        await this.financialDataVerificatorClient.updateIncome(this.incomeId, params);
        return;
      }

      const incomeData = await this.financialDataVerificatorClient.createIncome(params);
      this.incomeId = this.$getSafe(incomeData, 'data.id');
    },
    async calculateIncome(incomes) {
      try {
        const bodyIncome = [
          {
            type: 'DEBTOR',
            idNumber: this.idNumber,
            incomes
          }
        ];
        const { data: calculateIncome } = await this.financialDataVerificatorClient.calculateIncome(bodyIncome);
        const { summary = null, debtors = null } = calculateIncome;
        // only summary
        if (debtors) {
          // only debtors
          const debtorIncome = debtors.find(item => item.type === 'DEBTOR');
          const totalCard = this.formatValue(debtorIncome?.income?.monthlyNetIncome);

          const summaryCard = this.incomeCardSummary;
          const amount = debtorIncome?.income?.fixedAssets + debtorIncome?.income?.variableAssets + debtorIncome?.income?.periodicVariableAssets + debtorIncome?.income?.nonTaxableIncomes;
          summaryCard.income.value = this.formatValue(amount);
          summaryCard.income.children[0].value = this.incomeType === 'FIXED_TAXABLE' ? this.formatValue(debtorIncome?.income?.fixedAssets) : this.formatValue(debtorIncome?.income?.variableAssets);
          summaryCard.income.children[1].value = this.formatValue(debtorIncome?.income?.nonTaxableIncomes);
          summaryCard.income.children[2].value = this.formatValue(debtorIncome?.income?.periodicVariableAssets);
          const totalDiscount = debtorIncome?.income?.legalTaxes.legalTaxes + debtorIncome?.income?.otherDiscounts;
          summaryCard.discount.value = this.formatValue(totalDiscount);
          summaryCard.discount.children[0].value = this.formatValue(debtorIncome?.income?.legalTaxes.legalTaxes);
          summaryCard.discount.children[1].value = this.formatValue(debtorIncome?.income?.otherDiscounts);

          this.setIncome(bodyIncome, {
            totalIncome: this.formatValue(summary.totalIncome),
            totalIncomeUf: this.formatValue(summary.totalIncomeUf, 'UF')
          }, summaryCard, totalCard);
        }
      } catch (_error) {
        throw new Error(`No se pudo obtener la información general ${_error.message}`);
      }
    },
    setIncome(income, summary, incomeCardSummary, totalIncomeCardSummary) {
      const service = new EvaluationDataProcessor(this.$store);
      service.commitIncome({
        income,
        summary,
        incomeCardSummary,
        totalIncomeCardSummary
      });
    },
    formatValue(value, currency = '$') {
      return formatValueToCurrency(value, this.$i18n.locale, currency);
    },
    emitNewIncome() {
      this.$emit('new-income-created', this.hasNewIncome);
    },
    handleDocumentsUploaded(uploadedDocumentsLength) {
      this.$emit('income-documents-uploaded', uploadedDocumentsLength);
    },
    updateIncomeState() {
      const changeFixedMonthOne = this.initialIncome.fixedMonthOne !== (this.fixedMonthOne ? JSON.parse(this.fixedMonthOne) : null);
      const changeVariableMonthOne = this.initialIncome.variableMonthOne !== (this.variableMonthOne ? JSON.parse(this.variableMonthOne) : null);
      const changeVariableMonthTwo = this.initialIncome.variableMonthTwo !== (this.variableMonthTwo ? JSON.parse(this.variableMonthTwo) : null);
      const changeVariableMonthThree = this.initialIncome.variableMonthThree !== (this.variableMonthThree ? JSON.parse(this.variableMonthThree) : null);

      const allSwitchOff = Object.values(this.switchVisibility).every(value => !value);

      this.hasNewIncome = !allSwitchOff || changeFixedMonthOne || changeVariableMonthOne || changeVariableMonthTwo || changeVariableMonthThree;
      this.validateDocuments = this.hasNewIncome;

      this.emitNewIncome();
    }
  }
};
</script>

<style scoped>
.income-tag {
  width: 140px;
}

.income-type {
  line-height: 1;
}
</style>
<style>
.field-separation:not(:first-child) {
  border-top: 1px solid #EDEDED;
  padding: 1rem 0 0 0;
  margin-bottom: 0;
  margin-top: 0.2rem;
}
.field-separation:last-child {
  margin-bottom: 1rem;
}
.field-separation small,
.separator .field small{
  font-size: 0.6em;
}
</style>
